import { useCallback, useEffect, useRef } from "react";
import { ChatMessage } from "@/entities/messaging/types";

const PERMISSION_REQUESTED_PREFIX = "browser-push-permission-requested";
const DEDUP_CHANNEL = "push-notif-fired.dreamhub";
const DREAMHUB_ICON = "/favicon.ico";
const MAX_FIRED_KEYS = 500;

// Module-scope: no closure dependencies
const getMessageKey = (message: ChatMessage): string =>
  message.id ? `id:${message.id}` : `msg:${message.message}|ts:${message.timestamp}`;

interface UseBrowserPushNotificationsOptions {
  userId: string | undefined;
  enabled: boolean;
}

interface UseBrowserPushNotificationsReturn {
  /** `onClick` runs before the tab takes focus, when the user clicks the notification. */
  fireNotification: (message: ChatMessage, onClick?: () => void) => void;
}

export const useBrowserPushNotifications = ({
  userId,
  enabled
}: UseBrowserPushNotificationsOptions): UseBrowserPushNotificationsReturn => {
  const firedKeysRef = useRef(new Set<string>());
  const channelRef = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    if (!enabled || !userId || !("Notification" in window)) return;

    const permissionKey = `${PERMISSION_REQUESTED_PREFIX}:${userId}`;
    const stored = localStorage.getItem(permissionKey);
    if (Notification.permission === "default" && !stored) {
      // Store before prompting so a second render doesn't double-prompt.
      // Overwritten with the terminal result ('granted'/'denied') once resolved.
      localStorage.setItem(permissionKey, "pending");
      Notification.requestPermission().then((result) => {
        localStorage.setItem(permissionKey, result);
      });
    }
  }, [enabled, userId]);

  useEffect(() => {
    if (!enabled || typeof BroadcastChannel === "undefined") return;

    channelRef.current = new BroadcastChannel(DEDUP_CHANNEL);

    const handleMessage = (event: MessageEvent<unknown>) => {
      // Validate format before adding to dedup set to prevent poisoning by
      // same-origin scripts or extensions.
      if (
        typeof event.data === "string" &&
        (event.data.startsWith("id:") || event.data.startsWith("msg:") || event.data.startsWith("rand:"))
      ) {
        firedKeysRef.current.add(event.data);
      }
    };

    channelRef.current.addEventListener("message", handleMessage);

    return () => {
      channelRef.current?.removeEventListener("message", handleMessage);
      channelRef.current?.close();
      channelRef.current = null;
      firedKeysRef.current.clear();
    };
  }, [enabled]);

  const fireNotification = useCallback(
    (message: ChatMessage, onClick?: () => void) => {
      if (!enabled) return;
      if (!("Notification" in window) || Notification.permission !== "granted") return;
      if (!document.hidden) return;
      // Skip structured action payloads — they have no user-facing body text
      if (message.content?.kind === "action") return;

      const dedupeKey = getMessageKey(message);
      if (firedKeysRef.current.has(dedupeKey)) return;

      // Use a stable id as broadcast key; fall back to a random key to avoid
      // broadcasting message text to other tabs when no id is present.
      const broadcastKey = message.id ? `id:${message.id}` : `rand:${crypto.randomUUID()}`;

      try {
        const notification = new Notification("Dreamer", {
          body: message.message,
          icon: DREAMHUB_ICON,
          // Every tab hears a finished run; the OS keeps one notification per run
          // even when two tabs fire before the broadcast reaches either.
          ...(message.reportRunId ? { tag: message.reportRunId } : {})
        });

        notification.onclick = () => {
          onClick?.();
          window.focus();
          notification.close();
        };

        // Mark fired only after successful creation so failed notifications
        // are not permanently suppressed across tabs.
        firedKeysRef.current.add(dedupeKey);
        if (firedKeysRef.current.size > MAX_FIRED_KEYS) {
          const oldest = firedKeysRef.current.values().next().value;
          if (oldest !== undefined) firedKeysRef.current.delete(oldest);
        }
        channelRef.current?.postMessage(broadcastKey);
      } catch {
        // Browser may reject new Notification() even after permission is granted
        // (quota limit, sandboxed iframe, or OS-level suppression) — skip silently.
      }
    },
    [enabled]
  );

  return { fireNotification };
};
